import { useState } from "react";

import {    
    confirmPasswordValidator,
    confirmPasswordValidatorResetPassword,
    emailValidator,
    passwordValidator,
    resetPasswordValidator,
    usernameValidator
} from "../validators";

import { useLocalization } from "@progress/kendo-react-intl";

const touchErrors = (errors: any) => {
    return Object.entries(errors).reduce((acc: any, [field, fieldError]) => {
        if (typeof fieldError === 'object' && fieldError !== null) {
            acc[field] = {
                ...fieldError,
                dirty: true,
            };
        } else {
            acc[field] = {
                dirty: true,
            };
        }
        return acc;
    }, {});
};

export const useLoginFormValidator = (form: any) => {

    const localizationService = useLocalization();

    const [errors, setErrors] = useState({
        username: {
            dirty: false,
            error: false,
            message: "",
        },
        password: {
            dirty: false,
            error: false,
            message: "",
        },
        email: {
            dirty: false,
            error: false,
            message: "",
        },
        confirmPassword: {
            dirty: false,
            error: false,
            message: "",
        },
    });

    interface FormData {
        username: string;
        password: string;
        email?: string;
        confirmPassword?: string;
    }

    type ErrorField = keyof typeof errors;

    const validateForm = ({ form, field, errors, forceTouchErrors = false }: { form: FormData, field?: string, errors: any, forceTouchErrors?: boolean }) => {
        let isValid = true;

        // Create a deep copy of the errors
        let nextErrors: any = JSON.parse(JSON.stringify(errors));

        // Force validate all the fields
        if (forceTouchErrors) {
            nextErrors = touchErrors(errors);
        }
       
        const { username, password, email, confirmPassword } = form;

        if (nextErrors.username.dirty && (field ? field === "username" : true)) {
            const usernameMessage: any = usernameValidator(username, localizationService);
            nextErrors.username.error = !!usernameMessage;
            nextErrors.username.message = usernameMessage;
            if (!!usernameMessage) isValid = false;
        }
        
        if (nextErrors.password.dirty && (field ? field === "password" : true)) {
            const passwordMessage: any = passwordValidator(password, localizationService);
            nextErrors.password.error = !!passwordMessage;
            nextErrors.password.message = passwordMessage;
            if (!!passwordMessage) isValid = false;
        }
        
        setErrors(nextErrors);

        return {
            isValid,
            errors: nextErrors,
        };
    };
    
    const validateFormResetPassword = ({ form, field, errors, forceTouchErrors = false }: { form: FormData, field?: string, errors: any, forceTouchErrors?: boolean }) => {
        let isValid = true;

        // Create a deep copy of the errors
        let nextErrors: any = JSON.parse(JSON.stringify(errors));

        // Force validate all the fields
        if (forceTouchErrors) {
            nextErrors = touchErrors(errors);
        }
       
        const { username, password, email, confirmPassword } = form;

        if (nextErrors.username.dirty && (field ? field === "username" : true)) {
            const usernameMessage: any = usernameValidator(username, localizationService);
            nextErrors.username.error = !!usernameMessage;
            nextErrors.username.message = usernameMessage;
            if (!!usernameMessage) isValid = false;
        }
        
        if (nextErrors.password.dirty && (field ? field === "password" : true)) {
            const passwordMessage: any = resetPasswordValidator(password, localizationService);
            nextErrors.password.error = !!passwordMessage;
            nextErrors.password.message = passwordMessage;
            if (!!passwordMessage) isValid = false;
        }
        if (nextErrors.email.dirty && (field ? field === "email" : true)) {
            const emailMessage: any = emailValidator(email, localizationService);
            nextErrors.email.error = !!emailMessage;
            nextErrors.email.message = emailMessage;
            if (!!emailMessage) isValid = false;
        }

        if (
            nextErrors.confirmPassword.dirty &&
            (field ? field === "confirmPassword" : true)
        ) {
            const confirmPasswordMessage = confirmPasswordValidatorResetPassword(
                password,
                form, 
                localizationService
            );
            nextErrors.confirmPassword.error = !!confirmPasswordMessage;
            nextErrors.confirmPassword.message = confirmPasswordMessage;
            if (!!confirmPasswordMessage) isValid = false;
        }

        setErrors(nextErrors);

        return {
            isValid,
            errors: nextErrors,
        };
    };

    const onBlurField = (e: any) => {
        const field = e?.target?.name as keyof typeof errors;
        const fieldError = errors[field];
        if (fieldError.dirty) return;

        const updatedErrors: any = {
            ...errors,
            [field]: {
                ...errors[field],
                dirty: true,
            },
        };

        validateForm({ form, field, errors: updatedErrors });
    };
    
    const onBlurFieldResetPassword = (e: any) => {
        const field = e?.target?.name as keyof typeof errors;
        const fieldError = errors[field];
        if (fieldError?.dirty) return;

        const updatedErrors: any = {
            ...errors,
            [field]: {
                ...errors[field],
                dirty: true,
            },
        };

        validateFormResetPassword({ form, field, errors: updatedErrors });
    };

    const getFieldError = (field: ErrorField) => {
        const fieldState = errors[field];
        if (!fieldState?.dirty || !fieldState?.error) {
            return "";
        }

        return fieldState.message || "";
    };

    return {
        validateForm,
        onBlurField,
        validateFormResetPassword,
        onBlurFieldResetPassword,
        getFieldError,
        errors,
    };
};